// src/hooks/useCenterfyTracking.js
import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  trackPageView,
  trackEvent,
  trackClick,
  trackFormSubmit,
  initTracking,
  getUserId,
  linkSessionToUser,
} from "../utils/centerfyTracking";

/**
 * React hook for Centerfy tracking
 * Provides easy-to-use tracking functions and auto-tracks page views
 */
export const useCenterfyTracking = () => {
  const location = useLocation();

  // Initialize tracking on mount
  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        await initTracking();
      } catch (error) {
        console.error("Error initializing Centerfy tracking:", error);
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, []);

  // Auto-track page views on route change
  useEffect(() => {
    const trackCurrentPage = async () => {
      try {
        await trackPageView(location.pathname, {
          search: location.search,
          hash: location.hash,
        });
      } catch (error) {
        console.error("Error tracking page view:", error);
      }
    };

    // Small delay to ensure page is fully loaded
    const timeoutId = setTimeout(trackCurrentPage, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, location.hash]);

  // Link session to user when user logs in
  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      linkSessionToUser(userId);
    }
  }, []);

  // Memoized tracking functions
  const trackPage = useCallback(async (pagePath, metadata = {}) => {
    await trackPageView(pagePath, metadata);
  }, []);

  const trackCustomEvent = useCallback(async (eventType, eventData = {}) => {
    await trackEvent(eventType, eventData);
  }, []);

  const trackElementClick = useCallback(async (elementId, metadata = {}) => {
    await trackClick(elementId, metadata);
  }, []);

  const trackForm = useCallback(async (formId, formData = {}) => {
    await trackFormSubmit(formId, formData);
  }, []);

  return {
    trackPageView: trackPage,
    trackEvent: trackCustomEvent,
    trackClick: trackElementClick,
    trackFormSubmit: trackForm,
  };
};

export default useCenterfyTracking;

